import { differenceInMonths } from 'date-fns/differenceInMonths';
import { parseISO } from 'date-fns/parseISO';
import { fetchPost, fetchPrivatePost, fetchRelatedPosts, fetchPrivateRelatedPosts, fetchPopularPosts, fetchPrivateNextPosts, fetchNextPosts, fetchPinnedPosts, fetchPrivatePinnedPosts } from 'src/modules/community/singlePost/actions/fetchActions';
import { fetchCurrentCommunity } from 'src/modules/shared/context/actions';
import { fetchCommunityAbout } from 'src/modules/community/communityAbout/actions';
import { fetchCommunityTopics } from 'src/modules/community/shared/actions';
import { getExpectedPostUrl } from 'src/utils/router';
import { userIsCommunityMemberOrHUStaff } from 'src/utils/context';
import WithErrorHandling, { RequestError } from 'src/components/hoc/withErrorHandling';
import SinglePostPage from 'src/modules/community/singlePost/singlePostPage';
import { PageContext } from 'src/constants/types';

const Page = () => <SinglePostPage />;

Page.getInitialProps = async ({ res, req, store: { dispatch, getState }, query: { id, communitySlug }, asPath }: PageContext) => {
  const { currentUser } = getState().context;
  const isCommunityMemberOrHUStaff = userIsCommunityMemberOrHUStaff({ currentUser, communitySlug });

  // Fetch Post and Community
  if (isCommunityMemberOrHUStaff) {
    await Promise.all([
      dispatch(fetchPrivatePost(id, communitySlug)),
      dispatch(fetchCurrentCommunity(communitySlug)),
    ]);
  } else {
    await Promise.all([
      dispatch(fetchPost(id, communitySlug)),
      dispatch(fetchCurrentCommunity(communitySlug)),
    ]);
  }

  const state = getState();
  const { currentCommunity, communityErrorStatusCode } = state.context;
  const { post, postError } = state.pages.singlePost;

  // Handle Error
  if (postError || !post || !currentCommunity) {
    if (res && postError) res.statusCode = postError.status;
    const isHiddenPrivateCommunity = communityErrorStatusCode === 310;
    if (isHiddenPrivateCommunity) {
      if (!currentUser) throw new RequestError('This page is not available to visitors', 401);
      throw new RequestError('This is a private community', 404); // user is not a member
    }

    if (postError) {
      throw new RequestError('Post error', postError.status, currentCommunity);
    }

    return {};
  }

  // Fetch remaining requirements for page
  if (currentUser) {
    await Promise.all([
      dispatch(fetchCommunityAbout(communitySlug)),
      dispatch(fetchCommunityTopics(communitySlug)),
    ]);
    if (isCommunityMemberOrHUStaff) {
      await Promise.all([
        dispatch(fetchPrivatePinnedPosts(communitySlug)),
        dispatch(fetchPrivateNextPosts(id, post.dateCreated, communitySlug)),
        dispatch(fetchPrivateRelatedPosts(id, communitySlug)),
      ]);
    } else {
      await Promise.all([
        dispatch(fetchPinnedPosts(communitySlug)),
        dispatch(fetchRelatedPosts(id, communitySlug)),
        dispatch(fetchNextPosts(id, post.dateCreated, communitySlug)),
      ]);
    }
  } else {
    await Promise.all([
      dispatch(fetchCommunityAbout(communitySlug)),
      dispatch(fetchPopularPosts(communitySlug)),
      dispatch(fetchRelatedPosts(id, communitySlug)),
    ]);
  }

  // Only on initial page load on server, doesn't run on client-side transitions
  if (res && !!post && !!currentCommunity) {

    const expectedUrl = getExpectedPostUrl(post, currentCommunity.slug, decodeURI(asPath as string), req?.headers.host);
    const currentUrl = decodeURI(asPath as string);

    if (expectedUrl !== currentUrl) {
      res.writeHead(301, { Location: encodeURI(expectedUrl) });
      res.end();
      return {};
    }

    if (!currentUser) {
      // Set cache time. 5 minutes for new posts and 1 week for older ones
      const postAgeInMonths = differenceInMonths(new Date(), parseISO(post.dateLastActivity));
      const oneWeek = 60 * 60 * 24 * 7;
      const fiveMinutes = 60 * 5;
      const cacheTimeSeconds = postAgeInMonths >= 6 ? oneWeek : fiveMinutes;
      res.setHeader('Cache-Control', `max-age=${cacheTimeSeconds}, stale-while-revalidate=${cacheTimeSeconds}, no-store`);
    }
  }

  return {};
};

export default WithErrorHandling(Page);
